import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material"
import type React from "react"
import { useMemo, useState } from "react"
import { formatDateOnly } from "../../utils/dates"
import { extraColors } from "../../utils/theme"
import {
  DrawerSectionLabel,
  precomputedToggleGroupSx,
} from "./precomputedDrawerStyles"
import { NwssSortableTableHeader } from "./NwssSortableTableHeader"
import type { NwssSignalSample } from "./nwssSampleTypes"
import { NWSS_EVENT_DETECTIONS_LABEL } from "./nwssDisplayFormat"
import {
  NWSS_EVENT_DETECTION_ROW_LABEL,
  NWSS_RAW_TABLE_LEGEND_NOTE,
  nwssScopePrefix,
  type NwssDrawerSampleScope,
} from "./nwssDrawerScope"
import {
  formatPopulationServed,
  nwssReportingCountyDisplay,
} from "./useNwssSignalSamples"

const MAX_TABLE_ROWS = 2000

type TableSortKey = "date" | "site" | "county" | "pop"
type RawSampleFilter = "all" | "detections" | "event"

interface RawSamplesTableColumn {
  sortKey: TableSortKey | null
  label: string
  align?: "center" | "right"
  cellSx?: Record<string, unknown>
  sortable: boolean
}

export interface NwssRawTableProps {
  samples: NwssSignalSample[]
  showCountyColumn?: boolean
  /** All | Historic detections | Event detections while global scope is Historic. */
  showEventDetectionsFilter?: boolean
  reportingCounties?: string[] | null
  sampleScope?: NwssDrawerSampleScope
}

export const NwssRawTable: React.FC<NwssRawTableProps> = ({
  samples,
  showCountyColumn = false,
  showEventDetectionsFilter = false,
  reportingCounties = null,
  sampleScope = "historic",
}) => {
  const [sampleFilter, setSampleFilter] = useState<RawSampleFilter>("all")
  const [orderBy, setOrderBy] = useState<TableSortKey>("date")
  const [order, setOrder] = useState<"asc" | "desc">("asc")

  const effectiveSampleFilter = sampleScope === "event" ? "all" : sampleFilter

  const filteredSamples = useMemo(
    () => filterRawSamples(samples, effectiveSampleFilter),
    [samples, effectiveSampleFilter]
  )

  const rows = useMemo(
    () =>
      sortAndCapRawSamples(filteredSamples, orderBy, order, reportingCounties),
    [filteredSamples, orderBy, order, reportingCounties]
  )

  const handleSort = (key: TableSortKey) => {
    if (orderBy === key) {
      setOrder(prev => (prev === "asc" ? "desc" : "asc"))
      return
    }
    setOrderBy(key)
    setOrder(defaultOrderForRawSortKey(key))
  }

  const totalCount = filteredSamples.length

  return (
    <Box>
      <RawTableToolbar
        rowCount={rows.length}
        totalCount={totalCount}
        sampleFilter={sampleFilter}
        sampleScope={sampleScope}
        showEventDetectionsFilter={showEventDetectionsFilter}
        onFilterChange={setSampleFilter}
      />
      <NwssEventMemberLegend
        note={
          sampleScope === "event"
            ? "Showing event detections only. Each row defines this signal."
            : NWSS_RAW_TABLE_LEGEND_NOTE
        }
        showHighlight={sampleScope === "historic"}
      />
      <Box sx={{ maxHeight: 380, overflow: "auto" }}>
        <RawSamplesTable
          rows={rows}
          showCountyColumn={showCountyColumn}
          reportingCounties={reportingCounties}
          orderBy={orderBy}
          order={order}
          onSort={handleSort}
        />
      </Box>
    </Box>
  )
}

interface RawTableToolbarProps {
  rowCount: number
  totalCount: number
  sampleFilter: RawSampleFilter
  sampleScope: NwssDrawerSampleScope
  showEventDetectionsFilter: boolean
  onFilterChange: (filter: RawSampleFilter) => void
}

const RawTableToolbar: React.FC<RawTableToolbarProps> = ({
  rowCount,
  totalCount,
  sampleFilter,
  sampleScope,
  showEventDetectionsFilter,
  onFilterChange,
}) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      mb: 1,
    }}
  >
    <DrawerSectionLabel sx={{ mb: 0 }}>
      {formatRawSamplesTitle(sampleScope, rowCount, totalCount)}
    </DrawerSectionLabel>
    {sampleScope === "historic" && (
      <ToggleButtonGroup
        size="small"
        exclusive
        value={sampleFilter}
        onChange={(_event, value: RawSampleFilter | null) => {
          if (value != null) onFilterChange(value)
        }}
        sx={precomputedToggleGroupSx}
      >
        <ToggleButton value="all">All</ToggleButton>
        <ToggleButton value="detections" sx={{ textTransform: "none" }}>
          {`${nwssScopePrefix(sampleScope)} detections`}
        </ToggleButton>
        {showEventDetectionsFilter && (
          <ToggleButton value="event" sx={{ textTransform: "none" }}>
            {NWSS_EVENT_DETECTIONS_LABEL}
          </ToggleButton>
        )}
      </ToggleButtonGroup>
    )}
  </Box>
)

interface NwssEventMemberLegendProps {
  note: string
  showHighlight: boolean
}

const NwssEventMemberLegend: React.FC<NwssEventMemberLegendProps> = ({
  note,
  showHighlight,
}) => (
  <Box sx={{ mb: 1 }}>
    <Typography
      variant="caption"
      sx={{ color: extraColors.translucent50, display: "block", mb: 0.75 }}
    >
      {note}
    </Typography>
    {showHighlight && (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box sx={eventMemberLegendSwatchSx} />
        <Typography variant="caption" sx={{ color: extraColors.translucent50 }}>
          {NWSS_EVENT_DETECTION_ROW_LABEL}
        </Typography>
      </Box>
    )}
  </Box>
)

const formatRawSamplesTitle = (
  scope: NwssDrawerSampleScope,
  shown: number,
  total: number
): string => {
  const count =
    shown < total
      ? `${shown.toLocaleString()} of ${total.toLocaleString()}`
      : String(total)
  return `${nwssScopePrefix(scope)} raw samples (${count})`
}

interface RawSamplesTableProps {
  rows: NwssSignalSample[]
  showCountyColumn: boolean
  reportingCounties: string[] | null
  orderBy: TableSortKey
  order: "asc" | "desc"
  onSort: (key: TableSortKey) => void
}

const RawSamplesTable: React.FC<RawSamplesTableProps> = ({
  rows,
  showCountyColumn,
  reportingCounties,
  orderBy,
  order,
  onSort,
}) => (
  <Table size="small" stickyHeader sx={{ tableLayout: "fixed", width: "100%" }}>
    <RawSamplesTableHead
      showCountyColumn={showCountyColumn}
      orderBy={orderBy}
      order={order}
      onSort={onSort}
    />
    <TableBody>
      {rows.map(sample => (
        <RawSampleRow
          key={sample.record_id}
          sample={sample}
          showCountyColumn={showCountyColumn}
          reportingCounties={reportingCounties}
        />
      ))}
    </TableBody>
  </Table>
)

interface RawSamplesTableHeadProps {
  showCountyColumn: boolean
  orderBy: TableSortKey
  order: "asc" | "desc"
  onSort: (key: TableSortKey) => void
}

const RawSamplesTableHead: React.FC<RawSamplesTableHeadProps> = ({
  showCountyColumn,
  orderBy,
  order,
  onSort,
}) => (
  <TableHead>
    <TableRow>
      {rawSamplesTableColumns(showCountyColumn).map(column => {
        const sortKey = column.sortKey
        return column.sortable && sortKey != null ? (
          <NwssSortableTableHeader
            key={column.label}
            label={column.label}
            align={column.align}
            active={orderBy === sortKey}
            order={order}
            onClick={() => onSort(sortKey)}
            cellSx={column.cellSx}
          />
        ) : (
          <TableCell
            key={column.label}
            align={column.align}
            sx={{
              ...column.cellSx,
              color: extraColors.translucent50,
            }}
          >
            {column.label}
          </TableCell>
        )
      })}
    </TableRow>
  </TableHead>
)

interface RawSampleRowProps {
  sample: NwssSignalSample
  showCountyColumn: boolean
  reportingCounties: string[] | null
}

const RawSampleRow: React.FC<RawSampleRowProps> = ({
  sample,
  showCountyColumn,
  reportingCounties,
}) => {
  const countyLabel = nwssReportingCountyDisplay(sample, reportingCounties)

  return (
    <TableRow sx={rawSampleRowSx(sample.is_event_member)}>
      <TableCell sx={{ color: extraColors.white, whiteSpace: "nowrap" }}>
        {formatDateOnly(sample.sample_collect_date)}
      </TableCell>
      <TableCell sx={{ ...siteColumnSx, color: extraColors.white }}>
        {sample.site}
      </TableCell>
      {showCountyColumn && (
        <TableCell
          sx={{ ...countyColumnSx, color: extraColors.translucent50 }}
          title={countyLabel}
        >
          {countyLabel}
        </TableCell>
      )}
      <TableCell align="center" sx={detectColumnSx}>
        <RawSampleDetectBadge isDetected={sample.is_detected} />
      </TableCell>
      <TableCell align="right" sx={{ color: extraColors.translucent50 }}>
        {formatPopulationServed(sample.population_served ?? null)}
      </TableCell>
    </TableRow>
  )
}

interface RawSampleDetectBadgeProps {
  isDetected: boolean
}

const RawSampleDetectBadge: React.FC<RawSampleDetectBadgeProps> = ({
  isDetected,
}) => (
  <Box component="span" sx={rawSampleDetectBadgeSx(isDetected)}>
    {isDetected ? "yes" : "no"}
  </Box>
)

const siteColumnSx = {
  minWidth: 120,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
} as const

const detectColumnSx = {
  width: 130,
  minWidth: 130,
  whiteSpace: "nowrap",
  px: 1,
} as const

const countyColumnSx = {
  minWidth: 100,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
} as const

const eventMemberRowBg = extraColors.status.navyLightTransparent

const eventMemberLegendSwatchSx = {
  width: 14,
  height: 14,
  borderRadius: "2px",
  bgcolor: eventMemberRowBg,
  border: `1px solid ${extraColors.status.navyLight}`,
  flexShrink: 0,
} as const

const filterRawSamples = (
  samples: NwssSignalSample[],
  sampleFilter: RawSampleFilter
): NwssSignalSample[] => {
  switch (sampleFilter) {
    case "detections":
      return samples.filter(sample => sample.is_detected)
    case "event":
      return samples.filter(sample => sample.is_event_member)
    case "all":
      return samples
  }
}

const rawSortValueForKey = (
  sample: NwssSignalSample,
  orderBy: TableSortKey,
  reportingCounties: string[] | null
): number | string => {
  switch (orderBy) {
    case "date":
      return sample.sample_collect_date
    case "site":
      return sample.site
    case "county":
      return nwssReportingCountyDisplay(sample, reportingCounties)
    case "pop":
      return sample.population_served ?? -Infinity
  }
}

const sortAndCapRawSamples = (
  samples: NwssSignalSample[],
  orderBy: TableSortKey,
  order: "asc" | "desc",
  reportingCounties: string[] | null
): NwssSignalSample[] => {
  const sorted = [...samples].sort((a, b) => {
    const av = rawSortValueForKey(a, orderBy, reportingCounties)
    const bv = rawSortValueForKey(b, orderBy, reportingCounties)
    if (av < bv) return order === "asc" ? -1 : 1
    if (av > bv) return order === "asc" ? 1 : -1
    return 0
  })
  return sorted.slice(0, MAX_TABLE_ROWS)
}

const defaultOrderForRawSortKey = (key: TableSortKey): "asc" | "desc" =>
  key === "date" ? "asc" : "desc"

const rawSamplesTableColumns = (
  showCountyColumn: boolean
): RawSamplesTableColumn[] => [
  { sortKey: "date", label: "Date", sortable: true },
  { sortKey: "site", label: "Site", sortable: true, cellSx: siteColumnSx },
  ...(showCountyColumn
    ? [
        {
          sortKey: "county" as const,
          label: "County",
          sortable: true,
          cellSx: countyColumnSx,
        },
      ]
    : []),
  {
    sortKey: null,
    label: "Detect",
    align: "center",
    cellSx: detectColumnSx,
    sortable: false,
  },
  {
    sortKey: "pop",
    label: "Population served",
    align: "right",
    sortable: true,
  },
]

const rawSampleRowSx = (isEventMember: boolean) => ({
  bgcolor: isEventMember ? eventMemberRowBg : "transparent",
})

const rawSampleDetectBadgeSx = (isDetected: boolean) => ({
  color: isDetected
    ? extraColors.status.redLight
    : extraColors.status.greenLight,
  fontWeight: 700,
  fontSize: "0.72rem",
})
